/*
<summary>
   This functional component is all about rendering the Step 2 Crimp Tool Inspection table (AWG, Upper/Lower Limit CH, Tensile Strength Limit rows) with add/remove row, CSV template download and submit-time validation.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { useFormikContext } from 'formik';
import React, { useRef } from 'react';
import { Col, Row } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { useStore } from '../../../../contexts/store-provider';
import { downloadCsvFromRows } from '../../../../core/stores/utils/download-csv';
import { IToolCrimpRow } from '../../../../models/forms/IToolInspection';
import { IToolManagementVM } from '../../../../models/response/IToolManagementResponse';
import {
  ExportHolidayIcon,
  ImportIcon,
  PlusIcon,
  RemoveIcon,
} from '../../../../shared-components/svgs/svgs';
import { formatMessage } from '../../../../translations/format-message';
import {
  hasEmptyAwg,
  hasEmptyLowerLimit,
  hasEmptyTensileStrength,
  hasEmptyUpperLimit,
  isDuplicateAwg,
  isLowerLimitTooLarge,
  isTensileStrengthInvalid,
  isTensileStrengthTooLarge,
  isUpperLessThanLower,
  isUpperLimitTooLarge,
} from './step-two-validation';

interface IProps {
  // This flag is true only once the user has actually clicked Save/Update, so errors stay hidden while adding/typing a row.
  submitAttempted?: boolean;
}

/**
* This functional component renders the crimp inspection rows table and gates all row-level validation errors behind submitAttempted.
* @param props: submitAttempted flag
*/
const StepTwoCrimpInspection = ({ submitAttempted }: IProps) => {
  const { values, setFieldValue } = useFormikContext<IToolManagementVM>();
  const { toolManagementStore, preferencesStore } = useStore();
  const { language } = preferencesStore;
  const rows: IToolCrimpRow[] = values.CrimpRows || [];
  const touched = !!submitAttempted;
  const importInputRef = useRef<HTMLInputElement>(null);

  // AWG and Tensile Strength Limit are whole numbers only.
  const sanitizeIntegerInput = (raw: string): string => raw.replace(/[^0-9]/g, '');

  // Upper/Lower Limit CH allow positive decimals
  const sanitizeDecimalInput = (raw: string): string => {
    const digitsAndDots = raw.replace(/[^0-9.]/g, '');
    const firstDotIndex = digitsAndDots.indexOf('.');
    if (firstDotIndex === -1) return digitsAndDots;
    return (
      digitsAndDots.slice(0, firstDotIndex + 1) +
      digitsAndDots.slice(firstDotIndex + 1).replace(/\./g, '')
    );
  };

  /**
   * This function is used to restrict a row's numeric field to digits only at the keystroke
   * level (no native HTML5 number-input validation), which also blocks Enter from triggering
   * a premature form submit as a side effect since Enter isn't a digit/navigation key.
   * @param e: Keyboard event
   */
  const handleIntegerKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const navigationKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
    if (e.ctrlKey || e.metaKey) return;
    if (navigationKeys.includes(e.key)) return;
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  /**
   * This function is used to restrict a row's numeric field to digits and a single decimal
   * point at the keystroke level (no native HTML5 number-input validation), which also blocks
   * Enter from triggering a premature form submit as a side effect.
   * @param e: Keyboard event
   */
  const handleDecimalKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const navigationKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'];
    if (e.ctrlKey || e.metaKey) return;
    if (navigationKeys.includes(e.key)) return;

    const target = e.target as HTMLInputElement;
    if (e.key === '.' && target.value.includes('.')) {
      e.preventDefault();
      return;
    }
    if (!/^[0-9.]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  /**
   * This function is used to add a new empty crimp row with the next available row index.
   */
  const handleAddRow = () => {
    const nextIndex = rows.reduce((max, r) => Math.max(max, r.RowIndex), 0) + 1;
    setFieldValue('CrimpRows', [
      ...rows,
      {
        RowIndex: nextIndex,
        AWG: '',
        UpperLimitCH: null,
        LowerLimitCH: null,
        TensileStrengthLimit: null,
      },
    ]);
  };

  /**
   * This function is used to remove a crimp row by its row index.
   * @param rowIndex: Row index to remove
   */
  const handleRemoveRow = (rowIndex: number) => {
    setFieldValue(
      'CrimpRows',
      rows.filter((r) => r.RowIndex !== rowIndex)
    );
  };

  /**
   * This function is used to update a single field of a single crimp row.
   * @param rowIndex: Row index being edited
   * @param field: Field name to update
   * @param value: New value for the field
   */
  const handleFieldChange = (
    rowIndex: number,
    field: keyof IToolCrimpRow,
    value: string | number | null
  ) => {
    setFieldValue(
      'CrimpRows',
      rows.map((r) => (r.RowIndex === rowIndex ? { ...r, [field]: value } : r))
    );
  };

  /**
   * This function is used to generate and download an empty CSV template for the crimp inspection rows.
   */
  const handleDownloadTemplate = () => {
    const header =
      language === 'jp'
        ? 'AWG,上限CH(mm),下限CH(mm),引張強度下限(N)\n'
        : 'AWG,UpperLimitCH(mm),LowerLimitCH(mm),TensileStrengthLimit(N)\n';
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + header], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'CrimpToolInspectionTemplate.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * This function is used to open the native file picker for the crimp default-values CSV.
   */
  const handleImportClick = () => {
    importInputRef.current?.click();
  };

  /**
   * This function is used to parse the selected CSV (client-side, no server round-trip),
   * append the successfully parsed rows to the end of the table, and — when any row failed —
   * download a CSV of the failed rows with their reasons, mirroring the Import Tool Management
   * results-CSV pattern.
   * @param event: File input change event
   */
  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset now so selecting the same file again still fires this handler.
    event.target.value = '';
    if (!file) return;

    const { successRows, failedRows } = await toolManagementStore.ParseCrimpMasterCSVFile(
      file,
      rows
    );

    if (successRows.length > 0) {
      setFieldValue('CrimpRows', [...rows, ...successRows]);
    }

    if (failedRows.length > 0) {
      downloadCsvFromRows(
        [formatMessage('row_no'), formatMessage('awg'), formatMessage('message')],
        failedRows.map((row) => [row.RowNo, row.Awg, formatMessage(row.Message)]),
        'CrimpMasterImportFailedRows'
      );
    }

    if (successRows.length === 0 && failedRows.length === 0) {
      toast.error(formatMessage('RecordNotFound'));
    } else if (successRows.length === 0) {
      // Every row in the file failed to import.
      toast.error(formatMessage('ImportFailed'));
    } else if (failedRows.length > 0) {
      // Some rows succeeded, some failed.
      toast.success(formatMessage('PartialSuccess'));
    } else {
      // Every row in the file imported successfully.
      toast.success(formatMessage('ImportSuccess'));
    }
  };

  return (
    <div className="tool-crimp-step">
      <div className="tool-crimp-summary mb-3">
        <Row>
          <Col md={4}>
            <div className="tool-crimp-summary-label">
              <FormattedMessage id="tool_management_no" />
            </div>
            <div className="tool-crimp-summary-value fw-semibold">
              {values.ToolManagementNo || '-'}
            </div>
          </Col>
          <Col md={4}>
            <div className="tool-crimp-summary-label">
              <FormattedMessage id="tool_name_equipment_name" />
            </div>
            <div className="tool-crimp-summary-value fw-semibold">
              {values.ToolName || '-'}
            </div>
          </Col>
          <Col md={4}>
            <div className="tool-crimp-summary-label">
              <FormattedMessage id="serial_no" />
            </div>
            <div className="tool-crimp-summary-value fw-semibold">
              {values.SerialNo || '-'}
            </div>
          </Col>
        </Row>
      </div>

      <div className="tool-crimp-header d-flex align-items-center justify-content-between gap-3 mb-2">
        <div className="tool-crimp-awg-label fw-semibold">
          <FormattedMessage id="awg_default_values_list" />
        </div>
        <div className="tool-crimp-actions d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleAddRow}
          >
            <PlusIcon />
            <span className="ms-1"><FormattedMessage id="add_row" /></span>
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleImportClick}
          >
            <ImportIcon />
            <span className="ms-1"><FormattedMessage id="import_csv_file" /></span>
          </button>
          <input
            ref={importInputRef}
            type="file"
            accept=".csv"
            className="d-none"
            onChange={handleImportFile}
          />
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleDownloadTemplate}
          >
            <ExportHolidayIcon />
            <span className="ms-1"><FormattedMessage id="csv_template" /></span>
          </button>
        </div>
      </div>

      <div className="table-responsive tool-crimp-table">
        <table className="table">
          <thead>
            <tr>
              <th><FormattedMessage id="awg" /></th>
              <th><FormattedMessage id="upper_limit_ch" /></th>
              <th><FormattedMessage id="lower_limit_ch" /></th>
              <th><FormattedMessage id="tensile_strength_limit" /></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const awgEmpty = touched && hasEmptyAwg(row);
              const awgDuplicate = touched && isDuplicateAwg(row, rows);
              const upperEmpty = touched && hasEmptyUpperLimit(row);
              const lowerEmpty = touched && hasEmptyLowerLimit(row);
              const upperLessThanLower = touched && isUpperLessThanLower(row);
              const upperTooLarge = touched && isUpperLimitTooLarge(row);
              const lowerTooLarge = touched && isLowerLimitTooLarge(row);
              const tensileEmpty = touched && hasEmptyTensileStrength(row);
              const tensileInvalid = touched && isTensileStrengthInvalid(row);
              const tensileTooLarge = touched && isTensileStrengthTooLarge(row);

              return (
                <tr key={row.RowIndex}>
                  <td>
                    <input
                      type="text"
                      inputMode="numeric"
                      className={`form-control ${awgEmpty || awgDuplicate ? 'is-invalid' : ''}`}
                      placeholder="Example: 20"
                      value={row.AWG}
                      onChange={(e) =>
                        handleFieldChange(row.RowIndex, 'AWG', sanitizeIntegerInput(e.target.value))
                      }
                      onKeyDown={handleIntegerKeyDown}
                    />
                    {awgEmpty && (
                      <div className="invalid-feedback d-block errorMessage">
                        <FormattedMessage id="please_enter_awg" />
                      </div>
                    )}
                    {!awgEmpty && awgDuplicate && (
                      <div className="invalid-feedback d-block errorMessage">
                        <FormattedMessage id="duplicate_awg_not_allowed" />
                      </div>
                    )}
                  </td>
                  <td>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={`form-control ${
                        upperEmpty || upperLessThanLower || upperTooLarge ? 'is-invalid' : ''
                      }`}
                      placeholder="0.00"
                      value={row.UpperLimitCH ?? ''}
                      onChange={(e) =>
                        handleFieldChange(
                          row.RowIndex,
                          'UpperLimitCH',
                          sanitizeDecimalInput(e.target.value)
                        )
                      }
                      onKeyDown={handleDecimalKeyDown}
                    />
                    {upperEmpty && (
                      <div className="invalid-feedback d-block errorMessage">
                        <FormattedMessage id="please_enter_upper_limit" />
                      </div>
                    )}
                    {!upperEmpty && upperLessThanLower && (
                      <div className="invalid-feedback d-block errorMessage">
                        <FormattedMessage id="upper_limit_less_than_lower" />
                      </div>
                    )}
                    {!upperEmpty && !upperLessThanLower && upperTooLarge && (
                      <div className="invalid-feedback d-block errorMessage">
                        <FormattedMessage id="upper_limit_exceeds_max_value" />
                      </div>
                    )}
                  </td>
                  <td>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={`form-control ${lowerEmpty || lowerTooLarge ? 'is-invalid' : ''}`}
                      placeholder="0.00"
                      value={row.LowerLimitCH ?? ''}
                      onChange={(e) =>
                        handleFieldChange(
                          row.RowIndex,
                          'LowerLimitCH',
                          sanitizeDecimalInput(e.target.value)
                        )
                      }
                      onKeyDown={handleDecimalKeyDown}
                    />
                    {lowerEmpty && (
                      <div className="invalid-feedback d-block errorMessage">
                        <FormattedMessage id="please_enter_lower_limit" />
                      </div>
                    )}
                    {!lowerEmpty && lowerTooLarge && (
                      <div className="invalid-feedback d-block errorMessage">
                        <FormattedMessage id="lower_limit_exceeds_max_value" />
                      </div>
                    )}
                  </td>
                  <td>
                    <input
                      type="text"
                      inputMode="numeric"
                      className={`form-control ${
                        tensileEmpty || tensileInvalid || tensileTooLarge ? 'is-invalid' : ''
                      }`}
                      placeholder="0"
                      value={row.TensileStrengthLimit ?? ''}
                      onChange={(e) =>
                        handleFieldChange(
                          row.RowIndex,
                          'TensileStrengthLimit',
                          sanitizeIntegerInput(e.target.value)
                        )
                      }
                      onKeyDown={handleIntegerKeyDown}
                    />
                    {tensileEmpty && (
                      <div className="invalid-feedback d-block errorMessage">
                        <FormattedMessage id="please_enter_tensile_strength" />
                      </div>
                    )}
                    {!tensileEmpty && !tensileInvalid && tensileTooLarge && (
                      <div className="invalid-feedback d-block errorMessage">
                        <FormattedMessage id="tensile_strength_exceeds_max_value" />
                      </div>
                    )}
                    {!tensileEmpty && tensileInvalid && (
                      <div className="invalid-feedback d-block errorMessage">
                        <FormattedMessage id="tensile_strength_must_be_positive_integer" />
                      </div>
                    )}
                  </td>
                  <td className="text-center align-middle">
                    <button
                      type="button"
                      className="btn-icon btn-light-danger btn-sm"
                      onClick={() => handleRemoveRow(row.RowIndex)}
                    >
                      <RemoveIcon />
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="text-center text-muted p-4"
                >
                  <FormattedMessage id="no_rows_added" />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {submitAttempted && rows.length === 0 && (
        <div className="invalid-feedback d-block errorMessage">
          <FormattedMessage id="please_add_crimp_row" />
        </div>
      )}
    </div>
  );
};

export default StepTwoCrimpInspection;
